/**
 * Budgeted A* over block cells (vanilla PathFinder + WalkNodeEvaluator).
 *
 * Walk nodes: orthogonal and diagonal moves, 1-block step up, drops up to
 * `maxDrop`, diagonals only when both orthogonal neighbours are open, width /
 * height aware, vanilla-style node penalties (water 8, fire / cactus / magma
 * 8-16, web 8, lava / fire / fences blocked, doors per `doors`). Climbers treat
 * walls as floor; swimmers move through water in six directions.
 *
 * One shared instance with typed-array storage: a search allocates nothing.
 */

import type { World } from '../../world/chunk';
import {
  HOT_FLOOR, K_BLOCKED, K_CACTUS, K_DOOR_CLOSED, K_DOOR_OPEN, K_FENCE, K_FIRE, K_LAVA, K_OPEN, K_WATER, K_WEB, KIND, TOP,
} from './nodes';

/** Door handling. */
export const DOORS_NONE = 0;
export const DOORS_OPEN = 1;
export const DOORS_BREAK = 2;

/** What a mob can do, mutated in place by its navigator. */
export interface NavSpec {
  /** Footprint in cells and height in cells. */
  fw: number; fh: number;
  maxDrop: number;
  /** Extra cost of a water cell for land mobs; < 0 = water is blocked. */
  waterPenalty: number;
  /** Water mobs: only water cells are walkable. */
  swim: boolean;
  /** Striders: lava is walkable floor. */
  lava: boolean;
  /** Spiders: walls are floor. */
  climb: boolean;
  doors: 0 | 1 | 2;
}

export const NO_NODE = -1 << 20;
export const MAX_PATH = 128;
const CAP = 1024;
const HASH = 4096;

export const PATH_FAIL = 0;
export const PATH_FULL = 1;
export const PATH_PARTIAL = 2;

/** Search result: cell coordinates of the nodes from the first step to the end. */
export class PathResult {
  x = new Int32Array(MAX_PATH); y = new Int32Array(MAX_PATH); z = new Int32Array(MAX_PATH);
  len = 0;
  /** Reached the goal (false: stopped at the closest node found). */
  complete = false;
  /** Nodes expanded by the search (what the per-tick budget is charged). */
  expanded = 0;
}

export class PathFinder {
  private readonly nx = new Int32Array(CAP);
  private readonly ny = new Int32Array(CAP);
  private readonly nz = new Int32Array(CAP);
  private readonly g = new Float32Array(CAP);
  private readonly f = new Float32Array(CAP);
  private readonly h = new Float32Array(CAP);
  private readonly parent = new Int32Array(CAP);
  private readonly heapPos = new Int32Array(CAP);
  private readonly heap = new Int32Array(CAP);
  private heapN = 0;
  private count = 0;
  private readonly hashIdx = new Int32Array(HASH);
  private readonly hashStamp = new Int32Array(HASH);
  private stamp = 0;

  private w!: World;
  private s!: NavSpec;
  private fo = 0;
  /** Set by probe(): the last probed footprint contained a closed door. */
  sawDoor = false;
  /** Set by step(): cost of the last accepted move beyond its distance. */
  pen = 0;

  // ------------------------------------------------------------- cells

  /** Cost of occupying a footprint at (x, y, z), or -1 when blocked. */
  probe(x: number, y: number, z: number): number {
    const s = this.s, w = this.w;
    let pen = 0, wet = false;
    this.sawDoor = false;
    for (let dy = 0; dy < s.fh; dy++) {
      for (let dx = 0; dx < s.fw; dx++) {
        for (let dz = 0; dz < s.fw; dz++) {
          const k = KIND[w.getBlock(x + this.fo + dx, y + dy, z + this.fo + dz)];
          if (k === K_OPEN || k === K_DOOR_OPEN) continue;
          switch (k) {
            case K_LAVA: if (s.lava && dy > 0) break; return -1;
            case K_BLOCKED: case K_FENCE: case K_FIRE: return -1;
            case K_WATER:
              if (s.waterPenalty < 0 && !s.swim) return -1;
              if (dy === 0) wet = true;
              break;
            case K_CACTUS: return -1;
            case K_WEB: pen = Math.max(pen, 8); break;
            case K_DOOR_CLOSED:
              if (s.doors === DOORS_NONE) return -1;
              this.sawDoor = true; pen = Math.max(pen, s.doors === DOORS_BREAK ? 3 : 1);
              break;
          }
        }
      }
    }
    if (wet && !s.swim) pen = Math.max(pen, s.waterPenalty);
    return pen;
  }

  /** Is every cell of the footprint at (x, y, z) water (swimmers)? */
  private allWater(x: number, y: number, z: number): boolean {
    const s = this.s, w = this.w;
    for (let dy = 0; dy < s.fh; dy++) {
      for (let dx = 0; dx < s.fw; dx++) {
        for (let dz = 0; dz < s.fw; dz++) {
          if (KIND[w.getBlock(x + this.fo + dx, y + dy, z + this.fo + dz)] !== K_WATER) return false;
        }
      }
    }
    return true;
  }

  /** Floor under (x, y, z): 0 none, else 1 + extra cost (hot floor / water surface). */
  private floor(x: number, y: number, z: number): number {
    const s = this.s, w = this.w;
    let best = 0;
    for (let dx = 0; dx < s.fw; dx++) {
      for (let dz = 0; dz < s.fw; dz++) {
        const id = w.getBlock(x + this.fo + dx, y - 1, z + this.fo + dz);
        const k = KIND[id];
        if (k === K_LAVA) { if (s.lava) { best = Math.max(best, 1); continue; } return -1; }
        if (k === K_WATER) {
          if (s.waterPenalty < 0 && !s.swim) return -1;
          best = Math.max(best, 1 + s.waterPenalty);
          continue;
        }
        if (k === K_CACTUS) return -1;
        const t = TOP[id];
        if (t >= 0.4 && t <= 1.001 && k !== K_DOOR_CLOSED) best = Math.max(best, HOT_FLOOR[id] === 1 ? 17 : 1);
      }
    }
    if (best === 0 && s.climb && this.wallAt(x, y, z)) return 1;
    return best;
  }

  /** Climbers: is there a solid wall beside the footprint at this height? */
  private wallAt(x: number, y: number, z: number): boolean {
    const s = this.s, w = this.w, lo = this.fo - 1, hi = this.fo + s.fw;
    for (let i = 0; i < s.fw; i++) {
      const a = this.fo + i;
      if (TOP[w.getBlock(x + lo, y, z + a)] > 0.9 || TOP[w.getBlock(x + hi, y, z + a)] > 0.9
        || TOP[w.getBlock(x + a, y, z + lo)] > 0.9 || TOP[w.getBlock(x + a, y, z + hi)] > 0.9) return true;
    }
    return false;
  }

  /**
   * One orthogonal / diagonal move from (x, y, z): the new feet height or
   * NO_NODE. Sets `pen`.
   */
  step(x: number, y: number, z: number, dx: number, dz: number): number {
    const nx = x + dx, nz = z + dz;
    let p = this.probe(nx, y, nz);
    if (p >= 0) {
      const fl = this.floor(nx, y, nz);
      if (fl > 0) { this.pen = p + fl - 1; return y; }
      if (fl < 0) return NO_NODE;
      // walk off the edge: land within maxDrop
      for (let k = 1; k <= this.s.maxDrop; k++) {
        const q = this.probe(nx, y - k, nz);
        if (q < 0) return NO_NODE;
        const f2 = this.floor(nx, y - k, nz);
        if (f2 < 0) return NO_NODE;
        if (f2 > 0) { this.pen = q + f2 - 1 + (k > 3 ? 1 : 0); return y - k; }
      }
      return NO_NODE;
    }
    // blocked ahead: hop up one block when there is room overhead and on the landing
    if (this.probe(x, y + 1, z) < 0) return NO_NODE;
    p = this.probe(nx, y + 1, nz);
    if (p < 0) return NO_NODE;
    const fl = this.floor(nx, y + 1, nz);
    if (fl <= 0) return NO_NODE;
    this.pen = p + fl - 1;
    return y + 1;
  }

  // ------------------------------------------------------------ search

  private readonly dirs = [1, 0, -1, 0, 0, 1, 0, -1, 1, 1, 1, -1, -1, 1, -1, -1];

  private find(x: number, y: number, z: number): number {
    let i = ((x * 73856093) ^ (y * 19349663) ^ (z * 83492791)) & (HASH - 1);
    while (this.hashStamp[i] === this.stamp) {
      const n = this.hashIdx[i];
      if (this.nx[n] === x && this.ny[n] === y && this.nz[n] === z) return n;
      i = (i + 1) & (HASH - 1);
    }
    return -1;
  }
  private addNode(x: number, y: number, z: number, g: number, h: number, parent: number): number {
    const n = this.count++;
    this.nx[n] = x; this.ny[n] = y; this.nz[n] = z;
    this.g[n] = g; this.h[n] = h; this.f[n] = g + h; this.parent[n] = parent; this.heapPos[n] = -1;
    let i = ((x * 73856093) ^ (y * 19349663) ^ (z * 83492791)) & (HASH - 1);
    while (this.hashStamp[i] === this.stamp) i = (i + 1) & (HASH - 1);
    this.hashStamp[i] = this.stamp; this.hashIdx[i] = n;
    return n;
  }
  private up(i: number): void {
    const heap = this.heap, f = this.f, pos = this.heapPos;
    const n = heap[i];
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (f[heap[p]] <= f[n]) break;
      heap[i] = heap[p]; pos[heap[i]] = i; i = p;
    }
    heap[i] = n; pos[n] = i;
  }
  private push(n: number): void { this.heap[this.heapN] = n; this.heapPos[n] = this.heapN++; this.up(this.heapN - 1); }
  private pop(): number {
    const heap = this.heap, f = this.f, pos = this.heapPos;
    const top = heap[0];
    pos[top] = -2;
    const last = heap[--this.heapN];
    if (this.heapN > 0) {
      let i = 0;
      for (;;) {
        let c = 2 * i + 1;
        if (c >= this.heapN) break;
        if (c + 1 < this.heapN && f[heap[c + 1]] < f[heap[c]]) c++;
        if (f[heap[c]] >= f[last]) break;
        heap[i] = heap[c]; pos[heap[i]] = i; i = c;
      }
      heap[i] = last; pos[last] = i;
    }
    return top;
  }

  /** Point the cell queries at a world and a mob shape. */
  bind(w: World, spec: NavSpec): void {
    this.w = w; this.s = spec;
    this.fo = -((spec.fw - 1) >> 1);
  }

  /** Cell a mob centred at world coordinate `c` occupies along one axis. */
  cellOf(c: number, spec: NavSpec): number {
    return Math.floor(c + ((spec.fw - 1) >> 1) - spec.fw / 2 + 0.5);
  }
  /** World coordinate of a node's centre along one axis. */
  centreOf(n: number, spec: NavSpec): number { return n - ((spec.fw - 1) >> 1) + spec.fw / 2; }

  /**
   * Search from (sx, sy, sz) to the goal cell, expanding at most `maxNodes`
   * nodes within `range` blocks of the start. Fills `out`; returns PATH_*.
   */
  search(
    w: World, spec: NavSpec, sx: number, sy: number, sz: number,
    gx: number, gy: number, gz: number, maxNodes: number, range: number, out: PathResult,
  ): number {
    this.bind(w, spec);
    this.stamp++;
    this.count = 0; this.heapN = 0;
    out.len = 0; out.complete = false; out.expanded = 0;
    const cap = Math.min(maxNodes, CAP - 10);
    const r2 = range * range;
    const dist = (x: number, y: number, z: number): number => {
      // slightly greedy (x1.2): far fewer nodes for a path a few percent longer
      const a = x - gx, b = spec.climb ? 0 : y - gy, c = z - gz;
      return 1.2 * Math.sqrt(a * a + b * b + c * c);
    };
    const start = this.addNode(sx, sy, sz, 0, dist(sx, sy, sz), -1);
    this.push(start);
    let best = start, goal = -1;
    const swim = spec.swim, dirs = this.dirs;
    let expanded = 0;
    while (this.heapN > 0 && expanded < cap) {
      const cur = this.pop();
      expanded++;
      const cx = this.nx[cur], cy = this.ny[cur], cz = this.nz[cur];
      if (cx === gx && cz === gz && Math.abs(cy - gy) <= 1) { goal = cur; break; }
      if (this.h[cur] < this.h[best]) best = cur;
      const ng = this.g[cur];
      for (let d = 0; d < (swim ? 6 : spec.climb ? 10 : 8); d++) {
        let ex: number, ey: number, ez: number, cost: number;
        if (swim) {
          const sw = d < 4 ? d : -1;
          ex = cx + (sw >= 0 ? dirs[sw * 2] : 0);
          ez = cz + (sw >= 0 ? dirs[sw * 2 + 1] : 0);
          ey = cy + (d === 4 ? 1 : d === 5 ? -1 : 0);
          if (!this.allWater(ex, ey, ez)) continue;
          cost = 1;
        } else if (d >= 8) {
          // climbers scale the wall beside them, up or down
          if (!this.wallAt(cx, cy, cz)) continue;
          ey = cy + (d === 8 ? 1 : -1);
          const p = this.probe(cx, ey, cz);
          if (p < 0) continue;
          ex = cx; ez = cz; cost = 1 + p;
        } else {
          const ddx = dirs[d * 2], ddz = dirs[d * 2 + 1];
          if (d >= 4) {
            // diagonals only when both orthogonal neighbours are open at this height
            if (this.probe(cx + ddx, cy, cz) < 0 || this.probe(cx, cy, cz + ddz) < 0) continue;
          }
          ey = this.step(cx, cy, cz, ddx, ddz);
          if (ey === NO_NODE) continue;
          ex = cx + ddx; ez = cz + ddz;
          cost = (d >= 4 ? 1.4142 : 1) + this.pen;
        }
        if ((ex - sx) * (ex - sx) + (ez - sz) * (ez - sz) > r2) continue;
        const tg = ng + cost;
        let n = this.find(ex, ey, ez);
        if (n < 0) {
          if (this.count >= CAP) continue;
          n = this.addNode(ex, ey, ez, tg, dist(ex, ey, ez), cur);
          this.push(n);
        } else if (tg < this.g[n] && this.heapPos[n] !== -2) {
          this.g[n] = tg; this.f[n] = tg + this.h[n]; this.parent[n] = cur;
          this.up(this.heapPos[n]);
        }
      }
    }
    out.expanded = expanded;
    // vanilla: with no route, walk to the closest node found
    let end = goal;
    if (end < 0) {
      if (best === start) return PATH_FAIL;
      end = best;
    }
    // walk back to the start, then write the nodes in order (skipping the start cell)
    let n = 0;
    for (let p = end; p !== start && p >= 0; p = this.parent[p]) n++;
    if (n === 0) return PATH_FAIL;
    let write = Math.min(n, MAX_PATH);
    out.len = write;
    // when truncated, keep the first MAX_PATH nodes
    let skip = n - write;
    for (let p = end; p !== start && p >= 0; p = this.parent[p]) {
      if (skip > 0) { skip--; continue; }
      write--;
      out.x[write] = this.nx[p]; out.y[write] = this.ny[p]; out.z[write] = this.nz[p];
    }
    out.complete = goal >= 0 && n <= MAX_PATH;
    return out.complete ? PATH_FULL : PATH_PARTIAL;
  }
}
